/* Smart image picker — lets user choose camera OR gallery on mobile.
 * On desktop, opens the regular file dialog.
 *
 * Usage:
 *   <ImagePicker onSelect={(file) => ...}>
 *     {(open) => <button onClick={open}>Add photo</button>}
 *   </ImagePicker>
 */
import { useRef, useState, useEffect } from 'react';
import { Camera, ImageIcon, X } from 'lucide-react';

function isMobile() {
    if (typeof navigator === 'undefined') return false;
    return /android|iphone|ipad|ipod/i.test(navigator.userAgent);
}

export default function ImagePicker({ onSelect, children, testId = 'image-picker', multiple = false }) {
    const cameraRef = useRef(null);
    const galleryRef = useRef(null);
    const [showSheet, setShowSheet] = useState(false);
    const mobile = isMobile();

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') setShowSheet(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const open = () => {
        if (mobile) {
            setShowSheet(true);
        } else {
            galleryRef.current?.click();
        }
    };

    const handleChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length) onSelect(multiple ? files : files[0]);
        e.target.value = '';
        setShowSheet(false);
    };

    return (
        <>
            {children(open)}
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleChange} className="hidden" />
            <input ref={galleryRef} type="file" accept="image/*" multiple={multiple} onChange={handleChange} className="hidden" />

            {showSheet && (
                <div
                    className="fixed inset-0 z-[80] bg-brand-ink/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
                    onClick={() => setShowSheet(false)}
                    data-testid={`${testId}-sheet`}
                >
                    <div
                        className="bg-brand-card w-full sm:max-w-sm rounded-3xl border border-brand-border overflow-hidden animate-fade-up"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-5 pt-5 pb-2 flex items-start justify-between">
                            <h3 className="font-serif text-lg text-brand-ink">Add photo</h3>
                            <button type="button" aria-label="Close photo picker" onClick={() => setShowSheet(false)} className="p-1 -mr-1 -mt-1 hover:bg-brand-bg2 rounded-full text-brand-ink3"><X size={16} /></button>
                        </div>
                        <div className="p-3 space-y-2">
                            <button
                                type="button"
                                data-testid={`${testId}-camera`}
                                onClick={() => cameraRef.current?.click()}
                                className="w-full inline-flex items-center gap-3 p-4 rounded-2xl bg-brand-bg2 hover:bg-brand-bg border border-brand-border transition-colors"
                            >
                                <div className="w-10 h-10 rounded-2xl bg-brand-green text-white flex items-center justify-center"><Camera size={18} strokeWidth={1.8} /></div>
                                <div className="text-left">
                                    <div className="font-serif text-base text-brand-ink">Take photo</div>
                                    <div className="text-xs text-brand-ink3">Use your camera</div>
                                </div>
                            </button>
                            <button
                                type="button"
                                data-testid={`${testId}-gallery`}
                                onClick={() => galleryRef.current?.click()}
                                className="w-full inline-flex items-center gap-3 p-4 rounded-2xl bg-brand-bg2 hover:bg-brand-bg border border-brand-border transition-colors"
                            >
                                <div className="w-10 h-10 rounded-2xl bg-brand-ochre text-white flex items-center justify-center"><ImageIcon size={18} strokeWidth={1.8} /></div>
                                <div className="text-left">
                                    <div className="font-serif text-base text-brand-ink">Choose from gallery</div>
                                    <div className="text-xs text-brand-ink3">Pick an existing photo or file</div>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
